import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// React PDF Viewer imports
import {
  ScrollMode,
  SpecialZoomLevel,
  Viewer,
  Worker,
} from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
// import "../../../../styles/pdf-viewer.css";

export function FileViewerModal({ isOpen, onClose, file }) {
  const [textContent, setTextContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isPdf = file?.name?.toLowerCase().endsWith(".pdf");
  const isTextFile = file?.name
    ?.toLowerCase()
    .match(
      /\.(txt|md|json|js|jsx|ts|tsx|html|css|py|java|c|cpp|php|xml|yaml|yml|csv)$/
    );

  // Create default layout plugin instance with proper configuration
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    // Configure sidebar to show thumbnails by default and be open
    sidebarTabs: (defaultTabs) => [
      defaultTabs[0], // Thumbnail tab - this will be shown by default
      defaultTabs[1], // Bookmark tab
      defaultTabs[2], // Attachment tab
    ],
    // Configure the toolbar
    toolbarPlugin: {
      searchPlugin: {
        keyword: "",
      },
    },
  });

  // Reset state when modal opens/closes or file changes
  useEffect(() => {
    if (isOpen && file) {
      setTextContent("");
      setError(null);
      setIsLoading(false);

      // Load text content for text files
      if (isTextFile) {
        loadTextContent();
      }
    }
  }, [isOpen, file]);

  const loadTextContent = async () => {
    if (!file || !isTextFile) return;

    setIsLoading(true);
    setError(null);

    try {
      let url = file.publicUrl || file.visionMetadata?.publicUrl || file.url;

      if (!url) {
        throw new Error("No URL available for file");
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const text = await response.text();
      setTextContent(text);
    } catch (err) {
      console.error("Error loading text content:", err);
      setError(err.message);
      toast.error("Failed to load file content");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = () => {
    const url = file?.publicUrl || file?.visionMetadata?.publicUrl || file?.url;
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!file) return null;

  const getFileTypeDisplay = () => {
    if (isPdf) return "PDF Document";
    if (isTextFile) return "Text File";
    return "File";
  };

  const fileUrl = file.publicUrl || file.visionMetadata?.publicUrl || file.url;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] p-0 overflow-hidden flex flex-col bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
        hideCloseButton={true}
      >
        <DialogHeader className="px-6 py-3 flex-shrink-0 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg font-semibold truncate text-zinc-900 dark:text-zinc-100">
                {file.name}
              </DialogTitle>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                {getFileTypeDisplay()}
              </p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8 bg-transparent hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-900 dark:text-white border-none"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden bg-zinc-50 dark:bg-zinc-900">
          {isPdf && (
            <div
              className="h-full w-full"
              style={{ height: "calc(90vh - 100px)" }}
            >
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={fileUrl}
                  theme="dark"
                  defaultScale={SpecialZoomLevel.PageFit}
                  scrollMode={ScrollMode.Vertical}
                  plugins={[defaultLayoutPluginInstance]}
                  onDocumentLoadError={(error) => {
                    console.error("Error loading PDF:", error);
                    setError("Failed to load PDF document");
                    toast.error("Failed to load PDF document");
                  }}
                  onPageChange={(e) => {
                    console.log(`Current page: ${e.currentPage + 1}`);
                  }}
                  renderLoader={(percentages) => (
                    <div className="flex items-center justify-center h-64 bg-zinc-900">
                      <div className="text-center">
                        <p className="text-zinc-400 mb-2">Loading PDF...</p>
                        <div className="w-48 bg-zinc-700 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.round(percentages)}%` }}
                          />
                        </div>
                        <p className="text-zinc-500 text-sm mt-2">
                          {Math.round(percentages)}%
                        </p>
                      </div>
                    </div>
                  )}
                />
              </Worker>
            </div>
          )}

          {isTextFile && (
            <div className="h-full overflow-auto p-6">
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <p className="text-zinc-500 dark:text-zinc-400">
                    Loading file...
                  </p>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-64">
                  <p className="text-red-500 dark:text-red-400">{error}</p>
                </div>
              ) : (
                <div>
                  <pre className="whitespace-pre-wrap font-mono text-sm bg-white dark:bg-zinc-800 p-4 rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-auto max-h-[60vh] text-zinc-900 dark:text-zinc-100">
                    {textContent}
                  </pre>
                </div>
              )}
            </div>
          )}

          {!isPdf && !isTextFile && (
            <div className="flex items-center justify-center h-64">
              <p className="text-zinc-500 dark:text-zinc-400">
                Preview not available for this file type
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
